import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

const CHECKS = [
  { t: "Batch-specific Certificate of Analysis", d: "The single biggest separator. A trustworthy supplier publishes the COA for the exact batch you'd receive — batch number on the certificate matching the label on the vial. A generic \"our products are tested\" page without batch-level documents is a red flag." },
  { t: "Independent third-party testing", d: "The lab doing the testing should be independent of the supplier, with HPLC purity analysis as the minimum standard. Self-issued certificates carry no weight." },
  { t: "A verifiable UK business", d: "Look the company up at Companies House. A registered UK entity with a real trading history is accountable; an anonymous website is not." },
  { t: "UK-held stock and tracked dispatch", d: "Genuine UK stock means next-working-day delivery and no customs surprises. Suppliers drop-shipping from overseas often advertise \"UK\" while dispatching from abroad — check the dispatch promise (e.g. same-day before a stated cutoff) and whether tracking is provided." },
  { t: "Strict research-use-only positioning", d: "Research peptides are not licensed medicines. Any supplier making human-use or therapeutic claims is breaching UK medicines rules — which tells you how they treat compliance everywhere else." },
];

export default function GuideWhereToBuy() {
  useEffect(() => {
    document.title = "Where to Buy Research Peptides in the UK — 2026 Buyer's Checklist | Peptigen Labs";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "A practical 5-point checklist for choosing a UK research peptide supplier: batch-specific COAs, independent HPLC testing, verifiable UK business, UK stock and compliant labelling.");
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Where to Buy Research Peptides in the UK — 2026 Buyer's Checklist",
    description: "A practical 5-point checklist for choosing a UK research peptide supplier.",
    author: { "@type": "Organization", name: "Peptigen Labs Ltd" },
    publisher: { "@type": "Organization", name: "Peptigen Labs Ltd", url: "https://peptigenlabs.co.uk" },
    mainEntityOfPage: "https://peptigenlabs.co.uk/guides/where-to-buy-peptides-uk",
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-14" data-testid="guide-where-to-buy">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#00C2D6] mb-2">// Buyer's guide · updated 2026</div>
      <h1 className="text-4xl sm:text-5xl font-extrabold text-[#F7FAFC] mb-4">Where to Buy Research Peptides in the UK</h1>
      <p className="text-[#D5DCE8] text-base leading-relaxed mb-4">
        Searching for research peptides in the UK returns dozens of suppliers making near-identical claims.
        The differences that actually matter aren't on the homepage — they're in the documentation.
        Here is the 5-point checklist experienced researchers use before ordering from anyone (including us).
      </p>
      <div className="space-y-6 my-10">
        {CHECKS.map((c, i) => (
          <div key={i} className="flex gap-4 p-5 bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm">
            <CheckCircle2 className="w-5 h-5 text-[#A6FF00] shrink-0 mt-1" />
            <div>
              <h2 className="text-base md:text-lg font-bold text-[#F7FAFC] mb-1">{i + 1}. {c.t}</h2>
              <p className="text-sm text-[#9CA8B8] leading-relaxed">{c.d}</p>
            </div>
          </div>
        ))}
      </div>
      <h2 className="text-lg font-bold text-[#5EEAD4] mb-3 uppercase tracking-wider">How Peptigen Labs measures up</h2>
      <p className="text-sm text-[#D5DCE8] leading-relaxed mb-4">
        We built Peptigen Labs around this exact checklist: an independent, batch-specific COA is published on
        every product page; all stock is held in the UK with same-day dispatch on orders before 2pm and tracked
        24-hour delivery; Peptigen Labs Ltd is a registered UK company; and every product is supplied strictly
        for laboratory research use only.
      </p>
      <p className="text-sm text-[#9CA8B8] mb-10">
        Next: learn <Link to="/guides/how-to-read-a-peptide-coa" className="text-[#5EEAD4] underline" data-testid="guide-link-coa">how to read a peptide COA</Link> so
        you can verify any supplier's documents yourself, or browse our <Link to="/products" className="text-[#5EEAD4] underline">full catalogue</Link>.
      </p>
      <p className="text-xs text-[#7B8595]">All products are supplied strictly for laboratory research use only — not for human consumption.</p>
    </div>
  );
}
